import { Suspense, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { useSession } from "@/shared/auth/session";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { PageSkeleton } from "@/shared/components/PageSkeleton";
import { ROUTES } from "@/shared/constants/routes";
import { useDevicesQuery } from "@/shared/hooks/useDevicesQuery";
import { usePreference } from "@/shared/hooks/usePreference";
import { LiveProvider } from "@/shared/live/LiveProvider";
import { cn } from "@/shared/ui/cn";
import { ReconnectingNotice } from "./ReconnectingNotice";
import { ShellBanners } from "./ShellBanners";
import { Sidebar } from "./Sidebar";
import { SuspendedInterstitial } from "./SuspendedInterstitial";
import { TopBar } from "./TopBar";

const parseBool = (raw: string) => (raw === "true" ? true : raw === "false" ? false : null);

/**
 * **The shell as a ground and a sheet** (shell refresh, 2026-09-22).
 *
 * Before this, the sidebar, the top bar and the page all stood on the same white plane with
 * hairlines between them, and every screen read as one flat surface: the frame and the content had
 * the same colour, the same weight and the same edges. Now the frame is a **ground** (`--ground`),
 * the sidebar is the dark palette beside it, the top bar is a `--surface` band above it, and the page
 * is a **canvas**: one rounded, bordered sheet of `--bg` set in from the frame by `--canvas-gutter`.
 * Regions inside it remain `--surface`, so the page has a quiet cool tint and its working panels
 * stay crisp white rather than every layer collapsing onto the same plane.
 * The eye separates "where I can go" from "what I am looking at" by depth rather than by lines.
 *
 * Everything the old shell carried is still here: one SSE channel for the tree, the skip link, the
 * drawer under 1024 px, the banners, the suspended interstitial, the 1440 px cap on the page. The
 * organisation-and-account control moved from the top bar to the sidebar's foot, where the shell's
 * "who and where" now lives together (brand, workspace, menu, account); the top bar keeps what is
 * global and momentary — the way to anywhere (⌘K), the live channel and invitations. Theme sits
 * with organisation and account at the sidebar's foot.
 */
export function AppShell() {
  const location = useLocation();
  const { tenant } = useSession();
  // The api exempts `/billing` from the tenant-state guard so a suspended organisation can pay its way
  // back (ADR-0015 §5); the shell has to let that one tab through, or the exemption is unreachable.
  // M11 renamed it — `plan` and `billing` still land there, so all three addresses stay reachable.
  const settingsTab = new URLSearchParams(location.search).get("tab");
  const onSubscriptionTab =
    location.pathname === ROUTES.settings &&
    (settingsTab === "subscription" || settingsTab === "billing" || settingsTab === "plan");
  const suspended = tenant.status === "suspended" && !onSubscriptionTab;
  const [collapsed, setCollapsed] = usePreference("nacs.sidebar-collapsed", false, parseBool);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const devices = useDevicesQuery();
  const onlineCount = devices.data?.filter((d) => d.status === "online").length;
  const totalCount = devices.data?.length;

  return (
    // One SSE channel for the whole shell (rules/01 §5); every screen under it reads the cache.
    <LiveProvider>
      <div className="flex min-h-dvh bg-ground text-fg">
        {/* Nine shell stops stand between Tab and the page; this is the way past them. Hidden until
          it takes keyboard focus, then it is a normal button at the top-left. */}
        <a
          href="#main"
          className="sr-only rounded-md focus-visible:not-sr-only focus-visible:fixed focus-visible:left-4 focus-visible:top-3 focus-visible:z-50 focus-visible:border focus-visible:border-border-strong focus-visible:bg-surface-raised focus-visible:px-3 focus-visible:py-2 focus-visible:text-body focus-visible:font-medium focus-visible:text-fg focus-visible:no-underline"
        >
          Skip to content
        </a>
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          className="sticky top-0 hidden h-dvh lg:flex"
        />
        {drawerOpen ? (
          <div className="fixed inset-0 z-40 flex lg:hidden">
            <button
              type="button"
              aria-label="Close navigation"
              className="absolute inset-0 bg-overlay"
              onClick={() => setDrawerOpen(false)}
            />
            <Sidebar
              collapsed={false}
              drawer
              onToggle={() => undefined}
              onNavigate={() => setDrawerOpen(false)}
              className="relative z-10 h-dvh shadow-sm"
            />
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar
            onOpenMenu={() => setDrawerOpen(true)}
            {...(onlineCount !== undefined ? { onlineCount } : {})}
            {...(totalCount !== undefined ? { totalCount } : {})}
          />
          {/*
            The canvas. `pl-0` on desktop because the sidebar already stands `--canvas-gutter` in
            from its own edge; on the drawer layout the sheet takes the gutter on both sides.
          */}
          <div className="flex min-w-0 flex-1 flex-col px-(--canvas-gutter) pb-(--canvas-gutter) lg:pl-0">
            {/*
              `relative` makes the canvas the containing block for absolutely positioned descendants
              that have no positioned ancestor of their own, so its `overflow-hidden` actually clips
              them. Without it, Radix's hidden form inputs (the `aria-hidden` checkbox each Checkbox /
              Switch renders inside a <form>) sitting in a scrolled list (`max-h-* overflow-y-auto`)
              took the initial containing block, escaped the list's clipping, and stretched the
              document past the shell: a white band under the sidebar at the bottom of Face Kiosk
              settings (QA FK-1, 2270 px document on a 1600 px shell). Twenty-odd lists share that shape.
            */}
            <div
              className={cn(
                "relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-bg shadow-canvas",
              )}
            >
              <ReconnectingNotice />
              {suspended ? null : <ShellBanners />}
              {/* tabIndex -1 so the skip link's target actually takes focus, not just the scroll position. */}
              <main
                id="main"
                tabIndex={-1}
                className="w-full max-w-[1440px] flex-1 px-4 py-5 md:px-7 md:py-6"
              >
                {suspended ? (
                  <SuspendedInterstitial />
                ) : (
                  <ErrorBoundary resetKey={location.pathname}>
                    {/* Most routes under this shell are a list in a frame, so the route-level fallback
                        takes the table shape: it settles into the real geometry instead of reflowing
                        the moment the page arrives (pass 02 board `6a`). */}
                    <Suspense fallback={<PageSkeleton shape="table" />}>
                      <Outlet />
                    </Suspense>
                  </ErrorBoundary>
                )}
              </main>
            </div>
          </div>
        </div>
      </div>
    </LiveProvider>
  );
}
